import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PanelLeft, Search, Share2, UploadCloud, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AppearanceMenu, type Appearance } from "@/components/projects/appearance-menu";
import { FolderCard } from "@/components/projects/folder-card";
import { InviteToProjectDialog } from "@/components/projects/invite-to-project-dialog";
import { ProjectCardMenu } from "@/components/projects/project-card-menu";
import { ShareDialog, type ShareDialogItem } from "@/components/shares/share-dialog";
import { UploadMenu } from "@/components/projects/upload-menu";
import { VideoCard } from "@/components/projects/video-card";
import {
  VideoSortMenu,
  sortVideos,
  type VideoSortState,
} from "@/components/projects/video-sort-menu";
import { useAuth } from "@/hooks/use-auth";
import { useCreateFolder, useProjectFolders } from "@/hooks/use-folders";
import { useProject } from "@/hooks/use-projects";
import {
  useDeleteVideo,
  useDeleteVideoPermanent,
  useProjectVideos,
} from "@/hooks/use-videos";
import { canCreateShareLink, canInviteUsers } from "@/lib/api-types";
import { useWindowedList } from "@/hooks/use-windowed-list";
import { formatBytes } from "@/lib/utils";
import { SelectionActionBar } from "@/components/projects/selection-action-bar";
import { ProjectSidebar } from "@/components/projects/project-sidebar";
import {
  isDesktopViewport,
  useSearchOverlay,
} from "@/components/layout/search-overlay-context";

type Props = {
  /** "/app/projects" or "/admin/projects" — for the "All Projects" breadcrumb link. */
  projectsBasePath: string;
  /** "/app/upload" or "/admin/upload" — Upload menu items pre-fill ?projectId=. */
  uploadPath: string;
  /** "/app/watch" or "/admin/watch" — VideoCard double-click builds `${watchPath}/${videoId}`. */
  watchPath: string;
};

const GRID_CLASSES = "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
const LIST_CLASSES = "flex flex-col gap-2";

export function ProjectDetailPage({ projectsBasePath, uploadPath, watchPath }: Props) {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { role } = useAuth();

  const { data: project, isLoading: projectLoading, isError: projectError } = useProject(projectId);
  const { data: videos, isLoading: videosLoading, isError: videosError, refetch } =
    useProjectVideos(projectId, "root");
  const { data: folders } = useProjectFolders(projectId, "root");
  const createFolderMutation = useCreateFolder();
  const foldersBasePath = `${projectsBasePath}/${projectId}/folders`;

  const folderList = folders ?? [];

  async function handleCreateFolder(name: string) {
    if (!projectId) return;
    await createFolderMutation.mutateAsync({ projectId, name, parentFolderId: null });
  }

  const [appearance, setAppearance] = useState<Appearance>("grid");
  const [sort, setSort] = useState<VideoSortState>({ field: "dateUploaded", dirIndex: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Search overlay — desktop opens the shared overlay SCOPED to this project.
  // Mobile keeps the in-page inline expand so users can filter the current
  // project's cards without leaving the page.
  const { openWith: openSearchOverlay } = useSearchOverlay();
  const handleSearchIconClick = () => {
    if (isDesktopViewport()) {
      if (!projectId) return;
      openSearchOverlay({
        kind: "project",
        projectId,
        label: project?.name,
      });
    } else {
      setSearchOpen(true);
    }
  };
  const [inviteOpen, setInviteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());

  const deleteVideo = useDeleteVideo(projectId);
  const deleteVideoPermanent = useDeleteVideoPermanent(projectId);

  const selectedVideoObjects = useMemo(
    () => (videos ?? []).filter((v) => selectedIds.has(v.id)),
    [videos, selectedIds],
  );
  const selectedFolderObjects = useMemo(
    () => folderList.filter((f) => selectedFolders.has(f.id)),
    [folderList, selectedFolders],
  );

  function clearSelection() {
    setSelectedIds(new Set());
    setSelectedFolders(new Set());
  }

  const showInvite = canInviteUsers(role);
  const showShare = canCreateShareLink(role);
  const totalSelected = selectedIds.size + selectedFolders.size;
  const gridClass = appearance === "grid" ? GRID_CLASSES : LIST_CLASSES;

  const uploadHref = `${uploadPath}?projectId=${projectId}`;

  const visible = useMemo(() => {
    if (!videos) return [];
    const filtered = query.trim()
      ? videos.filter((v) => v.title?.toLowerCase().includes(query.trim().toLowerCase()))
      : videos;
    return sortVideos(filtered, sort);
  }, [videos, query, sort]);

  // Only mount a window of the (already sorted/filtered) grid; append on scroll.
  const { visible: pagedVideos, hasMore, sentinelRef } = useWindowedList(visible);

  const totalBytes = useMemo(
    () => (videos ?? []).reduce((sum, v) => sum + (v.fileSizeBytes ?? 0), 0),
    [videos]
  );

  function toggleSelected(id: string) {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleFolderSelected(id: string) {
    setSelectedFolders((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Card body single-click (2026-07-18) — replace the entire selection with
   * just this item. Wipes the other-type set too so switching a click between
   * videos and folders doesn't accumulate.
   */
  function selectVideoOnly(id: string) {
    setSelectedIds(new Set([id]));
    setSelectedFolders(new Set());
  }
  function selectFolderOnly(id: string) {
    setSelectedFolders(new Set([id]));
    setSelectedIds(new Set());
  }

  /** Resolve selected ids to ShareDialogItem[] — names come from the local lists. */
  const shareItems: ShareDialogItem[] = useMemo(() => {
    const videoItems: ShareDialogItem[] = (videos ?? [])
      .filter((v) => selectedIds.has(v.id))
      .map((v) => ({ type: "video" as const, id: v.id, name: v.title }));
    const folderItems: ShareDialogItem[] = folderList
      .filter((f) => selectedFolders.has(f.id))
      .map((f) => ({ type: "folder" as const, id: f.id, name: f.name }));
    return [...folderItems, ...videoItems];
  }, [videos, folderList, selectedIds, selectedFolders]);

  // Project was deleted (from this page or elsewhere) — 404 on refetch. Bounce back to the list
  // rather than showing a dead page.
  if (projectError) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-16 text-center md:px-8 md:py-24">
        <p className="text-muted-foreground">
          This project doesn't exist anymore — it may have been deleted.
        </p>
        <Button variant="outline" onClick={() => navigate(projectsBasePath)}>
          Back to all projects
        </Button>
      </div>
    );
  }

  const nothingAtAll =
    !videosLoading && !videosError && (videos?.length ?? 0) === 0 && folderList.length === 0;

  return (
    <div
      className="flex h-[100dvh] flex-col bg-background"
      onClick={(e) => {
        // Empty-space click clears the current selection (2026-07-18 spec).
        // Walk up the target chain — if it hits any card, the selection bar,
        // or any interactive control (button / link / input / menu item), the
        // click was NOT on whitespace and we leave selection alone.
        if (totalSelected === 0) return;
        const el = e.target as HTMLElement | null;
        if (!el || el.closest(
          '[data-selectable-card], [data-selection-bar], button, a, input, textarea, select, [role="menuitem"], [role="menu"], [role="dialog"]'
        )) return;
        clearSelection();
      }}
    >
      {/* Fixed top bar — collapse toggle + breadcrumb + global search + layout toggle */}
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-3 py-2 md:px-4">
        <button
          type="button"
          aria-label={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
          onClick={() => setSidebarOpen((v) => !v)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <div className="flex min-w-0 items-center gap-1.5 text-sm">
          <Link to={projectsBasePath} className="shrink-0 text-muted-foreground transition-colors hover:text-foreground">
            All Projects
          </Link>
          <span className="shrink-0 text-muted-foreground">/</span>
          <span className="truncate font-display text-base font-bold tracking-tight text-foreground">
            {projectLoading ? "…" : project?.name}
          </span>
        </div>
        <div className="flex-1" />
        {/* Global (workspace-wide) search */}
        <button
          type="button"
          aria-label="Search all projects"
          onClick={() => openSearchOverlay({ kind: "workspace" })}
          className="hidden h-8 w-full max-w-xs items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:flex"
        >
          <Search className="h-4 w-4 shrink-0" strokeWidth={2} />
          <span className="flex-1 text-left">Search…</span>
        </button>
        <button
          type="button"
          aria-label={sidebarOpen ? "Hide left panel" : "Show left panel"}
          onClick={() => setSidebarOpen((v) => !v)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <PanelLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </header>

      {/* Body — independently-scrolling sidebar + content column */}
      <div className="flex min-h-0 flex-1">
        {sidebarOpen && (
          <ProjectSidebar
            projectId={projectId}
            projectsBasePath={projectsBasePath}
            foldersBasePath={foldersBasePath}
            sharesBasePath={`${projectsBasePath}/${projectId}/shares`}
          />
        )}

        <div className="surface-panel flex min-w-0 flex-1 flex-col overflow-hidden rounded-t-xl">
          {/* Fixed toolbar — appearance, sort, project search + actions */}
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 text-sm md:px-6 md:gap-4">
            <AppearanceMenu value={appearance} onChange={setAppearance} />
            <VideoSortMenu value={sort} onChange={setSort} />
            {searchOpen ? (
              <div className="flex w-full max-w-xs items-center gap-1.5">
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search this project…"
                  className="h-8 text-sm"
                />
                <button
                  type="button"
                  aria-label="Close search"
                  onClick={() => { setSearchOpen(false); setQuery(""); }}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <X className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="Search this project"
                onClick={handleSearchIconClick}
                className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Search className="h-4 w-4" strokeWidth={1.75} />
              </button>
            )}

            <div className="flex-1" />

            <div className="flex shrink-0 items-center gap-2">
          <UploadMenu uploadHref={uploadHref} onCreateFolder={handleCreateFolder} />

          {showInvite && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Invite to this project"
                  onClick={() => setInviteOpen(true)}
                  className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground transition-colors hover:text-foreground"
                >
                  <UserPlus className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Invite to this project</TooltipContent>
            </Tooltip>
          )}

          {showShare && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Disabled when nothing selected — Frame.io parity, spec §"decision 8" */}
                <span>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShareOpen(true)}
                    disabled={totalSelected === 0}
                    aria-label="Share"
                  >
                    <Share2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Share</span>
                    {totalSelected > 0 && (
                      <span className="ml-1 rounded-full bg-primary-foreground/20 px-1.5 text-[10px] font-medium">
                        {totalSelected}
                      </span>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {totalSelected === 0 && (
                <TooltipContent side="bottom">Select items to share</TooltipContent>
              )}
            </Tooltip>
          )}

          {project && (
            <ProjectCardMenu project={project} onDeleted={() => navigate(projectsBasePath)} />
          )}
        </div>
      </div>

          {/* Scrolling content — the only scrollable region */}
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-6">
      {project && (
        <>
          <InviteToProjectDialog
            projectId={project.id}
            projectName={project.name}
            open={inviteOpen}
            onOpenChange={setInviteOpen}
          />
          <ShareDialog
            open={shareOpen}
            onOpenChange={setShareOpen}
            items={shareItems}
          />
        </>
      )}

      {/* Content */}
      <div className="pt-8">
        {nothingAtAll && !videosLoading && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-border py-24 text-center">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
              <UploadCloud className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-muted-foreground">Drag files and folders to begin.</p>
            <Button asChild>
              <Link to={uploadHref}>Upload</Link>
            </Button>
          </div>
        )}

        {/* Folders section — real backend rows (migration 0012). */}
        {folderList.length > 0 && (
          <div className="mb-8">
            <p className="mb-3 text-sm text-muted-foreground">
              {folderList.length} {folderList.length === 1 ? "Folder" : "Folders"}
            </p>
            <div className={gridClass}>
              {folderList.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  view={appearance}
                  foldersBasePath={foldersBasePath}
                  projectId={projectId!}
                  selected={selectedFolders.has(folder.id)}
                  onToggleSelected={() => toggleFolderSelected(folder.id)}
                  onSelectExclusive={() => selectFolderOnly(folder.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Assets section */}
        {!nothingAtAll && (
          <div>
            {(videos?.length ?? 0) > 0 && (
              <p className="mb-3 text-sm text-muted-foreground">
                {videos!.length} {videos!.length === 1 ? "Asset" : "Assets"} · {formatBytes(totalBytes)}
              </p>
            )}

            {videosLoading && (
              <div className={gridClass}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="aspect-video animate-pulse rounded-xl bg-secondary/60" />
                ))}
              </div>
            )}

            {videosError && !videosLoading && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <p className="text-muted-foreground">Couldn't load this project's videos.</p>
                <Button variant="outline" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            )}

            {!videosLoading && !videosError && videos && videos.length > 0 && visible.length === 0 && (
              <p className="py-16 text-center text-muted-foreground">No videos match "{query}".</p>
            )}

            {!videosLoading && !videosError && visible.length > 0 && (
              <>
                <div className={gridClass}>
                  {pagedVideos.map((video) => (
                    <VideoCard
                      key={video.id}
                      projectId={projectId!}
                      video={video}
                      view={appearance}
                      selected={selectedIds.has(video.id)}
                      onToggleSelected={() => toggleSelected(video.id)}
                      onSelectExclusive={() => selectVideoOnly(video.id)}
                      watchPath={watchPath}
                    />
                  ))}
                </div>
                {hasMore && <div ref={sentinelRef} aria-hidden className="h-1" />}
              </>
            )}
          </div>
        )}
      </div>
          </div>{/* scrolling content */}
        </div>{/* content column */}
      </div>{/* body */}

      {/* Selection action bar (2026-07-18). Fixed to viewport bottom when
          totalSelected > 0. Compare navigates to /app/compare/:a/:b (or the
          admin equivalent) — precondition-checked inside the bar. */}
      {totalSelected > 0 && (
        <SelectionActionBar
          selectedVideos={selectedVideoObjects}
          selectedFolders={selectedFolderObjects}
          onCleared={clearSelection}
          onDismiss={clearSelection}
          onTrashVideos={async (ids) => {
            await Promise.all(ids.map((id) => deleteVideo.mutateAsync(id)));
          }}
          onPermanentDeleteVideos={async (ids) => {
            await Promise.all(ids.map((id) => deleteVideoPermanent.mutateAsync(id)));
          }}
          onCompare={(a, b) => {
            const base = watchPath.startsWith("/admin/") ? "/admin/compare" : "/app/compare";
            navigate(`${base}/${a}/${b}`);
          }}
        />
      )}
    </div>
  );
}
